import React, { useMemo, useState } from "react";
import type { PortalArticlePayload } from "../../portal/portalDraftEnvelope";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export function draftBodyForInsight(p: PortalArticlePayload): string {
  const blocks = p.content_blocks || [];
  if (blocks.length) {
    return blocks
      .map((block) => {
        const text = (block.text || "").trim();
        const html = (block.html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        const line = text || html;
        const kind = (block.type || "").toLowerCase();
        const links = [...(block.urls || []), block.embed_url || ""].filter(Boolean).join(" ");
        if (kind === "header" && line) return `## ${line}`;
        if (line && links) return `${line}\n${links}`;
        if (line) return line;
        if (links) return links;
        if (kind === "image" && (block.src || "").trim()) return (block.description || block.src || "").trim();
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return (p.content || "").trim();
}

type Check = { id: string; label: string; ok: boolean; detail: string };

function wordsOf(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean);
}

function seoChecks(title: string, excerpt: string, body: string): Check[] {
  const headline = title.trim();
  const standfirst = excerpt.trim();
  const copy = body.trim();
  const wordCount = wordsOf(copy).length;
  const lead = (copy.split(/(?<=[.!?])\s+/)[0] || "").trim();
  const leadWords = wordsOf(lead).length;
  const hasLink = /https?:\/\/|<a\s|\[.+\]\(.+\)/i.test(copy);
  const hasSubhead = /^#{2,3}\s|<h[23]\b/im.test(copy);
  const headlineWords = wordsOf(headline.toLowerCase()).filter((w) => w.length > 3);
  const opening = wordsOf(copy.slice(0, 400).toLowerCase());
  const keywordInLead = headlineWords.some((w) => opening.includes(w));

  return [
    {
      id: "headline-length",
      label: "Headline length",
      ok: headline.length >= 45 && headline.length <= 70,
      detail: headline
        ? `${headline.length} characters. Aim for 45–70.`
        : "Add a headline.",
    },
    {
      id: "headline-case",
      label: "Headline is not shouted",
      ok: headline.length > 0 && headline !== headline.toUpperCase(),
      detail: "Sentence case reads as news. All caps reads as an ad.",
    },
    {
      id: "excerpt",
      label: "Standfirst",
      ok: standfirst.length >= 80 && standfirst.length <= 160,
      detail: standfirst
        ? `${standfirst.length} characters. Aim for 80–160.`
        : "Write a standfirst.",
    },
    {
      id: "length",
      label: "Body length",
      ok: wordCount >= 300,
      detail: `${wordCount} words. A reported piece wants at least 300.`,
    },
    {
      id: "lead",
      label: "Short lead",
      ok: leadWords > 0 && leadWords <= 40,
      detail: leadWords ? `The first sentence is ${leadWords} words.` : "The lead is empty.",
    },
    {
      id: "keyword",
      label: "Headline word in the opening",
      ok: keywordInLead,
      detail: "A concrete word from the headline should appear in the first lines.",
    },
    {
      id: "subhead",
      label: "A subheading",
      ok: hasSubhead,
      detail: hasSubhead ? "The body breaks into sections." : "Add a subheading so a scanner can enter.",
    },
    {
      id: "link",
      label: "A source link",
      ok: hasLink,
      detail: hasLink ? "The body points at a source." : "Link the claim a reader would want to check.",
    },
  ];
}

const accordionSx = {
  bgcolor: "transparent",
  color: "#fafafa",
  boxShadow: "none",
  "&:before": { display: "none" },
  border: "1px solid #27272a",
  borderRadius: "8px !important",
  mb: 1,
};

export function DraftSideInsights({
  title,
  excerpt,
  body,
}: {
  title: string;
  excerpt: string;
  body: string;
}) {
  const checks = useMemo(() => seoChecks(title, excerpt, body), [title, excerpt, body]);
  const ready = checks.filter((c) => c.ok).length;
  const missing = checks.length - ready;
  const [seoOpen, setSeoOpen] = useState(false);

  return (
    <Box sx={{ mb: 1.5, pr: 0.5 }}>
      <Accordion expanded={seoOpen} onChange={(_, open) => setSeoOpen(open)} disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#a1a1aa" }} />} sx={{ minHeight: 44, px: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", pr: 1 }}>
            <Typography variant="body2" sx={{ color: "#fafafa", fontWeight: 600, mr: 0.5 }}>
              SEO
            </Typography>
            <Chip size="small" label={`${ready} ok`} sx={{ bgcolor: "#14532d", color: "#bbf7d0", height: 22 }} />
            <Chip size="small" label={`${missing} missing`} sx={{ bgcolor: missing ? "#451a03" : "#27272a", color: missing ? "#fed7aa" : "#e4e4e7", height: 22 }} />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1.25 }}>
          {checks.map((check) => (
            <Box key={check.id} sx={{ display: "flex", gap: 1, py: 0.6, borderTop: "1px solid #27272a" }}>
              <Typography variant="caption" sx={{ color: check.ok ? "#4ade80" : "#fb923c", width: 16, flexShrink: 0 }}>
                {check.ok ? "✓" : "–"}
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ color: "#fafafa", fontSize: "0.8rem" }}>{check.label}</Typography>
                <Typography variant="caption" sx={{ color: "#a1a1aa" }}>{check.detail}</Typography>
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
